import {
  AgreementRecordSchema,
  AgreementStatusSchema,
  type AgreementRecord,
  type AgreementStatus,
} from '@ah/contracts';

/** Thrown for any non-2xx from the agreements endpoints. */
export class AgreementApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'AgreementApiError';
  }
}

/**
 * Therapist Agreement (ADR-019). The signature is a PNG data URL produced by
 * @ah/ui SignaturePad; the server encrypts it at rest and records IP/user-agent
 * itself — we deliberately send neither, so the audit trail cannot be forged
 * from the browser.
 */
export class AgreementApi {
  constructor(private readonly baseUrl: string) {}

  async getStatus(accessToken: string): Promise<AgreementStatus> {
    const res = await fetch(`${this.baseUrl}/api/v1/agreements/status`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) throw await this.toError(res);
    return AgreementStatusSchema.parse(await res.json());
  }

  async sign(
    accessToken: string,
    input: { agreementVersion: string; signatureBase64: string },
  ): Promise<AgreementRecord> {
    const res = await fetch(`${this.baseUrl}/api/v1/agreements/therapist-sign`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify(input),
    });
    if (!res.ok) throw await this.toError(res);
    return AgreementRecordSchema.parse(await res.json());
  }

  private async toError(res: Response): Promise<AgreementApiError> {
    let message = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: { message?: string } };
      if (body.error?.message) message = body.error.message;
    } catch {
      /* non-JSON body — keep the generic message */
    }
    return new AgreementApiError(message, res.status);
  }
}
